import { useState, useEffect } from 'react'
import { GroupService, type GroupWithMemberCount, type GroupWithMembers } from '../../services/groups'
import GroupManagementDialog from './GroupManagementDialog'
import { formatNumber } from '../../utils/formatNumber'

interface GroupListPanelProps {
  schoolId: string
  onGroupUpdated?: () => void
  isOpen: boolean
  onClose: () => void
}

const GroupListPanel = ({ schoolId, onGroupUpdated, isOpen, onClose }: GroupListPanelProps) => {
  const [groups, setGroups] = useState<GroupWithMemberCount[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingGroup, setEditingGroup] = useState<GroupWithMembers | null>(null)
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null)

  const loadGroups = async () => {
    if (!schoolId) return

    try {
      setIsLoading(true)
      setError('')
      const groupList = await GroupService.getSchoolGroups(schoolId)
      setGroups(groupList)
    } catch (err) {
      console.error('Failed to load groups:', err)
      setError('グループの読み込みに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      loadGroups()
    }
  }, [schoolId, isOpen])

  const handleCreateGroup = () => {
    setEditingGroup(null)
    setShowCreateDialog(true)
  }

  const handleEditGroup = async (groupId: string) => {
    try {
      const groupData = await GroupService.getGroupWithMembers(groupId)
      if (groupData) {
        setEditingGroup(groupData)
        setShowCreateDialog(true)
      }
    } catch (err) {
      console.error('Failed to load group for editing:', err)
      setError('グループの読み込みに失敗しました')
    }
  }

  const handleDeleteGroup = async (groupId: string) => {
    setDeletingGroupId(groupId)
  }

  const confirmDelete = async () => {
    if (!deletingGroupId) return

    try {
      await GroupService.deleteGroup(deletingGroupId)
      await loadGroups()
      onGroupUpdated?.()
      setDeletingGroupId(null)
    } catch (err) {
      console.error('Failed to delete group:', err)
      setError('グループの削除に失敗しました')
      setDeletingGroupId(null)
    }
  }

  const cancelDelete = () => {
    setDeletingGroupId(null)
  }

  const handleSaveGroup = async () => {
    setShowCreateDialog(false)
    setEditingGroup(null)
    await loadGroups()
    onGroupUpdated?.()
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">グループ管理</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto flex-1">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Create Button */}
            <div className="mb-6">
              <button
                onClick={handleCreateGroup}
                className="w-full px-4 py-3 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                新しいグループを作成
              </button>
            </div>

            {/* Groups List */}
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
              </div>
            ) : groups.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">グループがありません</h3>
                <p className="mt-1 text-sm text-gray-500">
                  新しいグループを作成してください
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {groups.map(group => (
                  <div
                    key={group.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{group.name}</h3>
                        {group.description && (
                          <p className="mt-1 text-sm text-gray-600">{group.description}</p>
                        )}
                        <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            {formatNumber(group.member_count)}名
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => handleEditGroup(group.id)}
                          className="px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 rounded-md hover:bg-purple-100 transition-colors"
                        >
                          編集
                        </button>
                        <button
                          onClick={() => handleDeleteGroup(group.id)}
                          className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                        >
                          削除
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              閉じる
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deletingGroupId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">グループを削除しますか？</h3>
            <p className="text-sm text-gray-600 mb-6">
              このグループを削除すると、グループに関連するデータも削除されます。この操作は取り消せません。
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                キャンセル
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                削除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <GroupManagementDialog
        isOpen={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false)
          setEditingGroup(null)
        }}
        schoolId={schoolId}
        group={editingGroup}
        onSave={handleSaveGroup}
      />
    </>
  )
}

export default GroupListPanel
